import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import ApiUrls from '../config/ApiUrls';
import { replaceByObject } from '../utils/Urls';
import API from './Api';
import {
  PostingApiResponse,
  TransactionApiResponse,
  TransactionSource,
} from './CalculationApi';
import { ExchangeRatesResponse } from './ExchangeRatesApi';
import { LoanAccountsApiResponse } from './GroupApi';
import { LoanAccountType } from '../data/models/LoanAccounts';

export class LoanTransactionPayment {
  creditNoteId?: string;
  invoiceId?: string;
  date: string;
  amount: number;
  bankAmount: number;
  currencyRate: number;
  paymentId?: string;
  prepaymentId?: string;
  overpaymentId?: string;

  public getEffectiveTransactionSource(
    sourceType: TransactionSource
  ): TransactionSource {
    if (this.overpaymentId || this.prepaymentId) {
      return sourceType == TransactionSource.ACCREC
        ? TransactionSource.ACCRECCREDIT
        : TransactionSource.ACCPAYCREDIT;
    }
    return TransactionSource.CASHREC;
  }

  public getEffectivePaymentId(): string {
    return this.overpaymentId ?? this.prepaymentId ?? this.paymentId;
  }
}

export type LoanTransactionApiResponse = Pick<
  TransactionApiResponse,
  | 'amount'
  | 'currency'
  | 'date'
  | 'description'
  | 'journalSourceId'
  | 'sourceType'
  | 'title'
> & {
  transactionId: number;
  sourceLineItemId: string;
  rechargeUrl?: string;
  payments?: LoanTransactionPayment[];
  amountDue?: number;
};

export type OrgLoanApiResponse = {
  id: number;
  balance: number;
  accountCode: string;
  monthInterestToDate: number;
  latestInterestPosting: PostingApiResponse;
  latestFxPosting: PostingApiResponse;
  cashInTransit: number;
};

export type LoanApiResponse = {
  id: number;
  account: LoanAccountsApiResponse;
  org1: OrgLoanApiResponse;
  org2: OrgLoanApiResponse;
  exchangeRates: ExchangeRatesResponse;
  interestRates: InterestRateResponse[];
  differenceCount?: number;
  type: LoanAccountType;
};

export type LoanTransactionMatchApiResponse = {
  date: string;
  type: string;
  org1Transactions: LoanTransactionApiResponse[];
  org2Transactions: LoanTransactionApiResponse[];
  org1Amount: number;
  org1Balance: number;
  org2Amount: number;
  org2Balance: number;
  userMatched: boolean;
};
export type LoanTransactionMatchApiRequest = LoanTransactionMatchApiResponse & {
  postingExternalId: string;
};

export type LoanTransactionDetailsApiResponse = {
  id: number;
  org1Id: number;
  org2Id: number;
  matches: LoanTransactionMatchApiResponse[];
  exchangeRates: ExchangeRatesResponse[];
  org1cashInTransitEOM: { [key: string]: number };
  org2cashInTransitEOM: { [key: string]: number };
};

export type InterestRateResponse = {
  date: string;
  rate: number;
};

export class LoansApi extends API {
  static getGroupLoans(
    groupId: number,
    date: DateTime
  ): Promise<LoanApiResponse[]> {
    return API.get(replaceByObject(ApiUrls.Loans, { groupId }), {
      params: {
        date: date.toISODate(),
      },
    }).then((response: AxiosResponse<LoanApiResponse[]>) => response.data);
  }

  static getLoanTransactions(
    groupId: number,
    loanId: number,
    date: DateTime = null
  ): Promise<LoanTransactionDetailsApiResponse> {
    return API.get(replaceByObject(ApiUrls.Loan, { groupId, loanId }), {
      params: {
        date: date?.toISODate(),
      },
    }).then(
      (response: AxiosResponse<LoanTransactionDetailsApiResponse>) =>
        response.data
    );
  }

  static setLoanOverrides(
    groupId: number,
    loanId: number,
    org1Id: number,
    org2Id: number,
    overrides: LoanTransactionMatchApiResponse[]
  ): Promise<LoanTransactionDetailsApiResponse> {
    return API.patch(
      replaceByObject(ApiUrls.LoanMatches, { groupId, loanId }),
      overrides,
      {
        params: {
          org1Id,
          org2Id,
        },
      }
    ).then(
      (response: AxiosResponse<LoanTransactionDetailsApiResponse>) =>
        response.data
    );
  }

  static setLoanInterestRates(
    groupId: number,
    loanId: number,
    interestRates: InterestRateResponse[]
  ): Promise<InterestRateResponse[]> {
    return API.put(
      replaceByObject(ApiUrls.LoanInterest, { groupId, loanId }),
      interestRates
    ).then((response: AxiosResponse<InterestRateResponse[]>) => response.data);
  }
}
